import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';

/**
 * OAuth callback page - handles the redirect from Cognito/SSO
 *
 * Note: The primary callback flow now redirects to root (/) where Amplify
 * processes the OAuth code automatically. This page exists as a fallback
 * for any existing /callback URLs configured in Cognito.
 *
 * The enable-oauth-listener is imported in main.tsx to ensure it's active
 * regardless of which route receives the callback.
 */
export function CallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for OAuth error parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (errorParam) {
      setError(`Authentication failed: ${errorDescription || errorParam}`);
    }
  }, []);

  useEffect(() => {
    // Redirect once authenticated — use saved destination if available, otherwise home
    if (isAuthenticated && !isLoading) {
      const savedUrl = sessionStorage.getItem('wellspring:redirect_after_login');
      if (savedUrl) {
        sessionStorage.removeItem('wellspring:redirect_after_login');
        navigate(savedUrl, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleRetry = () => {
    // Redirect to login page to try again
    navigate('/login', { replace: true });
  };

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen p-4'>
        <div className='text-red-500 mb-4'>
          <svg
            className='w-12 h-12'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>
        <h2 className='text-xl font-semibold mb-2'>Authentication Error</h2>
        <p className='text-muted-foreground mb-4 text-center max-w-md'>{error}</p>
        <button
          type='button'
          onClick={handleRetry}
          className='px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90'
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center justify-center min-h-screen'>
      <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4'></div>
      <p className='text-lg text-muted-foreground'>Completing sign in...</p>
      <p className='text-sm text-muted-foreground mt-2'>Please wait while we authenticate you...</p>
    </div>
  );
}
